import { DeleteOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  notification,
  Row,
  Select,
  Table,
  Typography,
  Upload,
} from "antd";
import {
  AFTER_DISBURSE_IOU_LIST,
  CERTIFICATE_OF_ORIGIN_FEE_ADD_ENDPOINT,
  CERTIFICATE_OF_ORIGIN_FEE_EDIT_ENDPOINT,
  SALES_CONTRACT_COMMERCIAL_LIST,
} from "apiServices/API_ENDPOINTS";
import { getData, postData } from "apiServices/common";
import moment from "moment";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const { Text } = Typography;

const DEFAULT_HEADER_ID = 11;
const EPB_TYPE_ENDPOINT = "/api/exim-cost/epb_type";
const EPB_ATTRIBUTE_ENDPOINT = "/api/exim-cost/epb_attribute";

/**
 * Main Component
 */
const CertificateOfOriginFeeForm = forwardRef((props, ref) => {
  const { form, isEdit, editInfo, viewMode } = props;
  const entriesWatcher = Form.useWatch("entries", form);
  const navigate = useNavigate();

  const [rexDeclarationValues, setRexDeclarationValues] = useState([0]);
  const [fileLists, setFileLists] = useState([[]]);

  const handleFileChange = useCallback((entryIndex) => ({ fileList: newFileList }) => {
    setFileLists((prev) => {
      const next = [...prev];
      next[entryIndex] = newFileList;
      return next;
    });
  }, []);

  const resetFiles = useCallback(() => {
    setFileLists([[]]);
    setRexDeclarationValues([0]);
  }, []);

  const addFile = useCallback((entryIndex, file) => {
    setFileLists((prev) => {
      const next = [...prev];
      next[entryIndex] = [...(next[entryIndex] || []), file];
      return next;
    });
  }, []);

  const [options, setOptions] = useState({
    salesContracts: [],
    invoices: [],
    attribute: [],
    iou: [],
  });

  const getSalesContractNumber = useCallback(async (filter) => {
    const body = {
      page: 1,
      per_page: 10,
      ...(filter?.reference_no && { reference_no: filter.reference_no }),
    };
    const response = await getData(`${SALES_CONTRACT_COMMERCIAL_LIST}`, null, body);
    if (response?.status === 200) {
      setOptions((prev) => ({
        ...prev,
        salesContracts: response?.data?.data?.data || [],
      }));
    }
  }, []);

  const getInvoiceNumbers = useCallback(async () => {
    const response = await getData(`/api/accounting/export_invoice_numbers`);
    if (response?.status === 200) {
      const invoicesData = response?.data?.data || [];
      setOptions((prev) => ({
        ...prev,
        invoices: invoicesData,
      }));
    }
  }, []);

  const getCharges = useCallback(async () => {
    const response = await getData(`/api/exim-cost/getTypebyHead/?cost_head_id=${DEFAULT_HEADER_ID}`);
    if (response?.status === 200) {
      const feeTypes = response?.data || [];

      const costFields = feeTypes.map((fee) => ({
        key: fee.id,
        type: fee.type.name,
        exim_charge_id: fee.id,
        exim_charge_amount: fee.amount || 0,
        isChecked: false,
      }));

      form.setFieldsValue({ tabs: costFields });
    }
  }, [form]);

  const getEPBTypes = useCallback(async () => {
    const response = await getData(`${EPB_TYPE_ENDPOINT}?page=1`);
    if (response?.status === 200) {
      const epbTypes = response?.data?.data?.data || [];

      const formattedTypes = epbTypes.map((type) => ({
        id: type.id,
        name: type.name,
        type: type.name,
      }));

      const updates = { epb_type_tabs: formattedTypes };

      if (!isEdit) {
        const entries = form.getFieldValue("entries") || [{}];
        form.setFieldsValue({
          ...updates,
          entries: entries.map((entry) => ({
            ...entry,
            epb_cost: entry?.epb_cost || [],
          })),
        });
      } else {
        form.setFieldsValue(updates);
      }

      setOptions((prev) => ({
        ...prev,
        epbTypes: epbTypes,
      }));
    }
  }, [form, isEdit]);

  const getEPBAttribute = useCallback(async () => {
    const response = await getData(`${EPB_ATTRIBUTE_ENDPOINT}?page=1`);
    if (response?.status === 200) {
      const attributeTypes = response?.data?.data?.data || [];
      setOptions((prev) => ({
        ...prev,
        attribute: attributeTypes,
      }));
    }
  }, []);

  const [apiCallsMade, setApiCallsMade] = useState({
    invoiceNumbers: false,
    epbTypes: false,
    epbAttributes: false,
    charges: false,
  });

  const [loading, setLoading] = useState(false);

  const fetchInitialData = useCallback(async () => {
    if (loading) return;

    setLoading(true);
    try {
      const apiCalls = [];

      if (!apiCallsMade.invoiceNumbers) {
        apiCalls.push(getInvoiceNumbers().then(() => setApiCallsMade((prev) => ({ ...prev, invoiceNumbers: true }))));
      }

      if (!apiCallsMade.epbAttributes) {
        apiCalls.push(getEPBAttribute().then(() => setApiCallsMade((prev) => ({ ...prev, epbAttributes: true }))));
      }

      if (!apiCallsMade.epbTypes) {
        apiCalls.push(getEPBTypes().then(() => setApiCallsMade((prev) => ({ ...prev, epbTypes: true }))));
      }

      if (!apiCallsMade.charges) {
        apiCalls.push(getCharges().then(() => setApiCallsMade((prev) => ({ ...prev, charges: true }))));
      }

      await Promise.all(apiCalls);

      if (!isEdit) {
        await getSalesContractNumber();
        resetFiles();
        if (!form.getFieldValue("entries")?.length) {
          form.setFieldsValue({
            entries: [{ cost: [], epb_cost: [] }],
          });
        }
      }
    } finally {
      setLoading(false);
    }
  }, [
    isEdit,
    resetFiles,
    getCharges,
    getInvoiceNumbers,
    getSalesContractNumber,
    getEPBAttribute,
    getEPBTypes,
    apiCallsMade,
    loading,
    form,
  ]);

  useEffect(() => {
    getIOU();
  }, []);

  useEffect(() => {
    if (
      !apiCallsMade.invoiceNumbers &&
      !apiCallsMade.epbTypes &&
      !apiCallsMade.epbAttributes &&
      !apiCallsMade.charges
    ) {
      fetchInitialData();
    }
  }, [fetchInitialData, apiCallsMade]);

  const processEditData = useCallback(() => {
    if (!editInfo || !isEdit) return;

    const existingCosts = editInfo.certificate_charges || [];
    const costFields = existingCosts.map((charge) => ({
      key: charge.exim_charge_id,
      type: charge.charges.type.name,
      exim_charge_id: charge.exim_charge_id,
      exim_charge_amount: charge.exim_charge_amount,
    }));

    let epbCostFields = [];
    let selectedEpbTypeIds = [];

    if (editInfo.certificate_epb_charges && editInfo.certificate_epb_charges.length > 0) {
      const epbTypeMap = {};

      editInfo.certificate_epb_charges.forEach((epb) => {
        const typeId = epb.epb_type_id;

        if (!selectedEpbTypeIds.includes(typeId)) {
          selectedEpbTypeIds.push(typeId);
        }

        if (!epbTypeMap[typeId]) {
          epbTypeMap[typeId] = {
            key: typeId,
            epb_type_id: typeId,
            epb_type_name: epb.epb_type?.name || "",
            attributes: {},
          };
        }

        const attribute = options.attribute.find((attr) => attr.id === epb.epb_attribute_id);
        if (attribute) {
          const attrFieldName = attribute.name.toLowerCase().replace(/\s+/g, "_");

          epbTypeMap[typeId].attributes[attrFieldName] = {
            epb_type_id: epb.epb_type_id,
            epb_attribute_id: epb.epb_attribute_id,
            amount: epb.amount,
          };
        }
      });

      epbCostFields = Object.values(epbTypeMap).map((type) => {
        const result = {
          key: type.epb_type_id,
          epb_type_id: type.epb_type_id,
          epb_type_name: type.epb_type_name,

          ...type.attributes,
        };

        return result;
      });
    }

    const rexValue = editInfo.rex_declaration || null;

    const entryValues = {
      invoice_id: editInfo.invoice_id,
      sales_contract_number: editInfo.export_details?.sales_contract_info?.reference_no || "",
      sales_contract_id: editInfo.export_details?.sales_contract_id,
      quantity: editInfo.export_details?.total_quantity,
      value: editInfo.export_details?.invoice_value,
      trn_date: editInfo.trn_date ? moment(editInfo.trn_date) : null,
      cost: costFields,
      epb_cost: epbCostFields,
      rrex_declaration: rexValue,
      iou_id: editInfo?.iou_info?.id,
    };

    form.setFieldsValue({ entries: [entryValues] });
    setRexDeclarationValues([parseFloat(rexValue) || 0]);

    if (editInfo?.iou_info) {
      const iouExists = options.iou.filter((iou) => iou.id === editInfo.iou_info.id).length > 0;

      if (!iouExists) {
        setOptions((prev) => ({
          ...prev,
          iou: [
            {
              id: editInfo.iou_info.id,
              iou_number: editInfo.iou_info.iou_number,
            },
            ...prev.iou,
          ],
        }));
      }
    }

    if (editInfo.export_details?.sales_contract_info?.reference_no) {
      getSalesContractNumber({
        reference_no: editInfo.export_details?.sales_contract_info?.reference_no,
      });
    }

    if (editInfo?.voucher) {
      setFileLists({});

      const filename = editInfo.voucher.split("/").pop();

      addFile(0, {
        uid: "-1",
        name: filename,
        status: "done",
        url: process.env.REACT_APP_BASE_URL + "/" + editInfo.voucher,
      });
    }
  }, [editInfo, isEdit, form, addFile, getSalesContractNumber, options.attribute, options.iou]);

  useEffect(() => {
    if (isEdit && editInfo) {
      if (options.attribute.length > 0) {
        processEditData();

        setTimeout(() => {
          form.validateFields([["entries", 0, "epb_cost"]]).catch(() => {});
        }, 100);
      } else {
        fetchInitialData().then(() => {
          processEditData();

          setTimeout(() => {
            form.validateFields([["entries", 0, "epb_cost"]]).catch(() => {});
          }, 100);
        });
      }
    }
  }, [isEdit, editInfo, fetchInitialData, processEditData, options.attribute.length, form]);

  const onSelectInvoice = useCallback(
    (entryName) => async (value) => {
      try {
        const selectedInvoice = options.invoices.find((invoice) => invoice.id === value);

        if (selectedInvoice) {
          const entries = [...(form.getFieldValue("entries") || [])];
          entries[entryName] = {
            ...entries[entryName],
            sales_contract_id: selectedInvoice.sales_contract_id,
            sales_contract_number: selectedInvoice.sales_contract_info?.reference_no || "",
            quantity: selectedInvoice.total_quantity || "",
            value: selectedInvoice.invoice_value || "",
          };

          form.setFieldsValue({ entries });

          await getCharges();
        }
      } catch (error) {}
    },
    [options.invoices, form, getCharges],
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const buildFormData = useCallback(
    (values, entryFileList, rexDeclarationValue) => {
      const costs =
        values.cost?.map((cost) => ({
          exim_charge_id: cost.exim_charge_id,
          exim_charge_amount: cost.exim_charge_amount,
        })) || [];

      const epbCosts = [];

      values.epb_cost?.forEach((epbCostItem) => {
        const epbTypeId = epbCostItem.epb_type_id;

        const attributes = options.attribute.filter((attr) => attr.is_required === "1");

        attributes.forEach((attr) => {
          const fieldName = attr.name.toLowerCase().replace(/\s+/g, "_");
          const fieldValue = epbCostItem[fieldName];

          if (fieldValue && typeof fieldValue === "object") {
            if (fieldValue.amount) {
              epbCosts.push({
                epb_type_id: epbTypeId,
                epb_attribute_id: attr.id,
                amount: fieldValue.amount,
                field_type: attr.field_type || "text",
              });
            }
          }
        });
      });

      const formData = new FormData();

      formData.append("invoice_id", values.invoice_id);

      if (values?.iou_id) {
        formData.append("iou_id", values.iou_id);
      }

      if (values.sales_contract_id) {
        formData.append("sales_contract_id", values.sales_contract_id);
      }

      if (values.trn_date) {
        formData.append("trn_date", values.trn_date.format("YYYY-MM-DD"));
      }

      formData.append("cost", JSON.stringify(costs));

      if (epbCosts.length > 0) {
        formData.append("epb_cost", JSON.stringify(epbCosts));
      }

      if (rexDeclarationValue) {
        formData.append("rex_declaration", rexDeclarationValue);
      }

      const formEpbCosts = values.epb_cost || [];
      let epbTotal = 0;

      if (formEpbCosts.length > 0) {
        formEpbCosts.forEach((cost) => {
          const requiredAttributes = options.attribute.filter((attr) => attr.is_required === "1");

          requiredAttributes.forEach((attr) => {
            if (attr.field_type === "textarea") {
              return;
            }

            const fieldName = attr.name.toLowerCase().replace(/\s+/g, "_");
            const fieldValue = cost[fieldName];

            if (fieldValue && typeof fieldValue === "object" && fieldValue.amount) {
              epbTotal += parseFloat(fieldValue.amount) || 0;
            }
          });
        });
      }

      if (epbTotal > 0) {
        formData.append("epb_total", epbTotal.toFixed(2));
      }

      const costTotal = (values.cost || []).reduce((sum, cost) => {
        return sum + (parseFloat(cost.exim_charge_amount) || 0);
      }, 0);

      if (costTotal > 0) {
        formData.append("total", costTotal.toFixed(2));
      }

      const rexAmount = parseFloat(rexDeclarationValue) || 0;
      const grandTotal = (epbTotal + rexAmount + costTotal).toFixed(2);

      if (rexAmount > 0) {
        formData.append("rex_declaration", rexAmount.toString());
      }

      formData.append("grand_total", grandTotal);

      if (entryFileList?.length > 0) {
        if (entryFileList[0].originFileObj) {
          formData.append("voucher", entryFileList[0].originFileObj);
        } else if (isEdit && !entryFileList[0].originFileObj) {
        }
      }

      return formData;
    },
    [options.attribute, isEdit],
  );

  const submitOneEntry = useCallback(
    async (entryValues, entryFileList, rexDeclarationValue) => {
      const formData = buildFormData(entryValues, entryFileList, rexDeclarationValue);

      const query = isEdit
        ? `${CERTIFICATE_OF_ORIGIN_FEE_EDIT_ENDPOINT}/${editInfo?.id}`
        : CERTIFICATE_OF_ORIGIN_FEE_ADD_ENDPOINT;

      const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

      let response;
      const maxRetries = 2;

      response = await postData(query, formData);

      if (response && (response?.code === 200 || response?.status === 200)) {
        return response;
      }

      if (response?.message?.includes("Too Many Attempts")) {
        for (let i = 0; i < maxRetries; i++) {
          const waitTime = 1000 * (i + 1);
          await delay(waitTime);

          response = await postData(query, formData);

          if (response && (response?.code === 200 || response?.status === 200)) {
            return response;
          }

          if (!response?.message?.includes("Too Many Attempts")) {
            throw new Error(response?.message || "Failed to submit form");
          }
        }

        throw new Error("Too many attempts. Please try again later.");
      }

      throw new Error(response?.message || "Failed to submit form");
    },
    [buildFormData, isEdit, editInfo],
  );

  const handleSubmit = useCallback(
    async (values) => {
      if (isSubmitting) {
        notification.warning({
          message: "Please wait",
          description: "Form is currently being submitted...",
          placement: "topRight",
        });
        return;
      }

      setIsSubmitting(true);

      try {
        const entries = values.entries || [];

        const submitPromises = entries.map((entry, index) => {
          const entryFileList = fileLists[index] || [];
          const rexValue = rexDeclarationValues[index] ?? entry?.rex_declaration ?? 0;
          return submitOneEntry(entry, entryFileList, rexValue);
        });

        const results = await Promise.all(submitPromises);

        if (results?.length) {
          notification.success({
            message: "Success",
            description:
              results[0]?.message || `Certificate of Origin Fee ${isEdit ? "updated" : "created"} successfully`,
            placement: "topRight",
          });
          navigate("/import-export-expense-tracking/certificate-of-origin-fee");
        }
      } catch (error) {
        notification.error({
          message: "Error",
          description: "Failed to submit form. Please try again.",
          placement: "topRight",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [fileLists, isEdit, navigate, isSubmitting, rexDeclarationValues, submitOneEntry],
  );

  useImperativeHandle(ref, () => ({ form }), [form]);

  const handleEpbSelectionChange = useCallback(
    (entryName) => (checkedValues) => {
      const allEPBs = form.getFieldValue("epb_type_tabs") || [];
      const entries = [...(form.getFieldValue("entries") || [])];
      const currentEPBs = entries[entryName]?.epb_cost || [];

      const currentREXValue = entries[entryName]?.rrex_declaration;

      const valueMap = {};

      currentEPBs.forEach((item) => {
        if (item && item.epb_type_id) {
          valueMap[item.epb_type_id] = item;
        }
      });

      const selected = allEPBs
        .filter((epb) => checkedValues.includes(epb.id))
        .map((epb) => {
          if (valueMap[epb.id]) {
            return {
              ...valueMap[epb.id],
              key: epb.id,
              epb_type_id: epb.id,
              epb_type_name: epb.name || epb.type,
            };
          }

          return {
            key: epb.id,
            epb_type_id: epb.id,
            epb_type_name: epb.name || epb.type,
          };
        });

      const rexValue = currentREXValue || 0;

      entries[entryName] = {
        ...entries[entryName],
        epb_cost: selected,
        rrex_declaration: rexValue,
      };

      form.setFieldsValue({ entries });

      setRexDeclarationValues((prev) => {
        const next = [...prev];
        next[entryName] = parseFloat(rexValue) || 0;
        return next;
      });

      setTimeout(() => {
        form.validateFields([["entries", entryName, "epb_cost"], ["entries", entryName, "rrex_declaration"]]).catch(
          () => {},
        );
      }, 100);
    },
    [form],
  );

  const tableColumns = useMemo(
    () => [
      {
        title: "Title",
        dataIndex: "fieldName",
        key: "id",
        width: "40%",
        render: (fieldName) => (
          <>
            <Form.Item
              name={[fieldName, "type"]}
              style={{ width: "100%", marginBottom: "0" }}
              rules={[{ required: true, message: "Please select a Description" }]}
            >
              <Input disabled={true} />
            </Form.Item>
          </>
        ),
      },
      {
        title: "Total",
        dataIndex: "fieldName",
        key: "exim_charge_amount",
        width: "60%",
        render: (fieldName) => (
          <Form.Item
            name={[fieldName, "exim_charge_amount"]}
            noStyle
            rules={[
              {
                required: true,
                message: "Please enter amount",
              },
            ]}
          >
            <InputNumber
              placeholder="Enter amount"
              disabled={viewMode}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
              min={0}
              precision={2}
              style={{ width: "100%" }}
            />
          </Form.Item>
        ),
      },
    ],
    [viewMode],
  );

  const getIOU = async (filter) => {
    const body = {
      page: 1,
      per_page: 10,
      ...(filter?.iou_number && { iou_number: filter.iou_number }),
    };
    const response = await getData(`${AFTER_DISBURSE_IOU_LIST}`, null, body);

    if (response?.status === 200) {
      setOptions((prev) => ({
        ...prev,
        iou: response?.data?.data || [],
      }));
    }
  };

  const getEntryTotal = (entryName) => {
    const costs = entriesWatcher?.[entryName]?.cost || [];
    return (
      costs.reduce((sum, cost) => {
        const amount = parseFloat(cost.exim_charge_amount) || 0;
        return sum + amount;
      }, 0) || 0
    );
  };

  const allowMultiple = !isEdit && !viewMode;

  return (
    <div className="containt-body">
      <Card title={""} bordered={false} style={{ boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)" }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={true}
          initialValues={{ entries: [{ cost: [], epb_cost: [] }] }}
        >
          <Form.List name="entries">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => {
                  const entryFileList = fileLists[field.name] || [];
                  const rexDeclarationValue = rexDeclarationValues[field.name] ?? 0;
                  const total = getEntryTotal(field.name);

                  return (
                    <div key={field.key} style={{ marginBottom: allowMultiple ? "24px" : 0 }}>
                      {allowMultiple && fields.length > 1 && (
                        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "8px" }}>
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => {
                              remove(field.name);
                              setFileLists((prev) => prev.filter((_, i) => i !== field.name));
                              setRexDeclarationValues((prev) => prev.filter((_, i) => i !== field.name));
                            }}
                          />
                        </div>
                      )}

                      <Card
                        title={<Text strong>Invoice Information</Text>}
                        style={{ marginBottom: "24px", borderRadius: "8px" }}
                        size="small"
                      >
                        <Row gutter={24}>
                          <Col xs={24} sm={12} md={8} lg={6}>
                            <Form.Item
                              label="Invoice Number"
                              name={[field.name, "invoice_id"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please select an invoice number",
                                },
                              ]}
                            >
                              <Select
                                placeholder="Select invoice"
                                onSelect={onSelectInvoice(field.name)}
                                disabled={viewMode}
                                showSearch
                                filterOption={(input, option) =>
                                  option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                                options={options.invoices.map((invoice) => ({
                                  label: invoice.invoice_number,
                                  value: invoice.id,
                                }))}
                                style={{ width: "100%" }}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12} md={8} lg={6}>
                            <Form.Item label="Sales Contract Number" name={[field.name, "sales_contract_number"]}>
                              <Input
                                disabled={true}
                                placeholder="Auto-populated from Invoice"
                                style={{ background: "#f5f5f5", color: "#333" }}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12} md={8} lg={6}>
                            <Form.Item label="Quantity" name={[field.name, "quantity"]}>
                              <Input
                                disabled={true}
                                placeholder="Auto-filled from Invoice"
                                style={{ background: "#f5f5f5", color: "#333" }}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12} md={8} lg={6}>
                            <Form.Item label="Value" name={[field.name, "value"]}>
                              <Input
                                disabled={true}
                                placeholder="Auto-filled from Invoice"
                                style={{ background: "#f5f5f5", color: "#333" }}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row gutter={24}>
                          <Col xs={24} sm={12} md={8}>
                            <Form.Item label="TRN Date" name={[field.name, "trn_date"]}>
                              <DatePicker
                                style={{ width: "100%" }}
                                disabled={viewMode}
                                placeholder="Select TRN Date"
                                format="YYYY-MM-DD"
                              />
                            </Form.Item>
                          </Col>
                          <Col span={6}>
                            <Form.Item label="IOU" name={[field.name, "iou_id"]}>
                              <Select
                                placeholder="Select IOU"
                                disabled={viewMode}
                                allowClear
                                showSearch
                                filterOption={false}
                                onSearch={async (value) => {
                                  if (value && value.length >= 2) {
                                    await getIOU({ iou_number: value });
                                  } else if (!value) {
                                    await getIOU();
                                  }
                                }}
                                onSelect={(value) => {
                                  const selectedIOU = options.iou.find((iou) => iou.id === value);
                                  if (selectedIOU) {
                                    const entries = [...(form.getFieldValue("entries") || [])];
                                    entries[field.name] = {
                                      ...entries[field.name],
                                      iou_id: selectedIOU.id,
                                    };
                                    form.setFieldsValue({ entries });
                                  }
                                }}
                                options={options.iou.map((iou) => ({
                                  label: iou.iou_number,
                                  value: iou.id,
                                }))}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12} md={8}>
                            <Form.Item
                              label="Voucher"
                              name={[field.name, "voucher"]}
                              rules={[
                                {
                                  required: false,
                                  message: "Please upload a voucher file",
                                },
                              ]}
                            >
                              <Upload
                                fileList={entryFileList}
                                onChange={handleFileChange(field.name)}
                                beforeUpload={() => false}
                                maxCount={1}
                                disabled={viewMode}
                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                              >
                                <Button
                                  icon={<UploadOutlined />}
                                  disabled={viewMode}
                                  type="default"
                                  style={{
                                    borderRadius: "4px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  {isEdit && entryFileList.length > 0 ? "Change File" : "Upload Voucher"}
                                </Button>
                              </Upload>
                            </Form.Item>
                          </Col>
                        </Row>
                      </Card>

                      <Card style={{ marginBottom: "16px" }}>
                        <Row>
                          <Col span={24}>
                            {form.getFieldValue("tabs")?.length === 0 && (
                              <Text type="warning">
                                No fee types available. Please select an invoice first or contact admin.
                              </Text>
                            )}
                            {form.getFieldValue("tabs")?.length > 0 && (
                              <>
                                <div
                                  style={{
                                    marginBottom: "12px",
                                    paddingBottom: "8px",
                                    borderBottom: "1px solid #e8e8e8",
                                    display: "flex",
                                    gap: "4px",
                                  }}
                                >
                                  <Text strong style={{ fontSize: "16px" }}>
                                    Select Authority:
                                  </Text>
                                </div>
                                <div
                                  className="fee-tabs-header"
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "12px",
                                    marginBottom: "12px",
                                    paddingBottom: "4px",
                                  }}
                                >
                                  <Checkbox.Group
                                    onChange={(checkedValues) => {
                                      const allTabs = form.getFieldValue("tabs") || [];
                                      const entries = [...(form.getFieldValue("entries") || [])];
                                      const currentCost = entries[field.name]?.cost || [];

                                      const amountMap = {};
                                      currentCost.forEach((item) => {
                                        amountMap[item.exim_charge_id] = item.exim_charge_amount;
                                      });

                                      const selectedFees = allTabs
                                        .filter((fee) => checkedValues.includes(fee.key))
                                        .map((fee) => ({
                                          key: fee.key,
                                          type: fee.type,
                                          exim_charge_id: fee.id || fee.key,

                                          exim_charge_amount:
                                            amountMap[fee.key] !== undefined
                                              ? amountMap[fee.key]
                                              : fee.exim_charge_amount || 0,
                                        }));

                                      entries[field.name] = {
                                        ...entries[field.name],
                                        cost: selectedFees,
                                      };
                                      form.setFieldsValue({ entries });
                                    }}
                                    value={
                                      form
                                        .getFieldValue(["entries", field.name, "cost"])
                                        ?.map((fee) => fee.exim_charge_id) || []
                                    }
                                    style={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      width: "100%",
                                      gap: "12px",
                                    }}
                                  >
                                    {form.getFieldValue("tabs").map((fee) => {
                                      const isSelected = form
                                        .getFieldValue(["entries", field.name, "cost"])
                                        ?.some((item) => item.exim_charge_id === fee.key);

                                      return (
                                        <Checkbox
                                          key={fee.key}
                                          className={`fee-tab ${isSelected ? "fee-tab-selected" : ""}`}
                                          style={{
                                            padding: "8px 16px",
                                            border: `1px solid ${isSelected ? "#1890ff" : "#d9d9d9"}`,
                                            borderRadius: "6px",
                                            cursor: "pointer",
                                            backgroundColor: isSelected ? "#e6f7ff" : "#ffffff",
                                            position: "relative",
                                            marginRight: "8px",
                                            display: "flex",
                                            boxShadow: isSelected ? "0 2px 6px rgba(24,144,255,0.2)" : "none",
                                            transition: "all 0.3s ease",
                                            marginBottom: "10px",
                                          }}
                                          value={fee.key}
                                          disabled={viewMode}
                                        >
                                          <span
                                            style={{
                                              fontWeight: isSelected ? "500" : "normal",
                                              color: isSelected ? "#1890ff" : "#333",
                                            }}
                                          >
                                            {fee.type}
                                          </span>
                                        </Checkbox>
                                      );
                                    })}
                                  </Checkbox.Group>
                                </div>
                              </>
                            )}
                          </Col>
                        </Row>
                        <Divider orientation="left">Fee Details</Divider>
                        <Form.List name={[field.name, "cost"]}>
                          {(costFields) => (
                            <>
                              {costFields.length > 0 ? (
                                <Table
                                  bordered
                                  dataSource={costFields.map((costField) => ({
                                    ...costField,
                                    key: costField.key,
                                    fieldName: costField.name,
                                  }))}
                                  pagination={false}
                                  columns={tableColumns}
                                  summary={() => (
                                    <Table.Summary fixed>
                                      <Table.Summary.Row style={{ background: "#f0f7ff" }}>
                                        <Table.Summary.Cell index={0} colSpan={1}>
                                          <Text strong style={{ fontSize: "16px" }}>
                                            Total
                                          </Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={1} colSpan={4}>
                                          <Text strong>{parseFloat(total || 0).toFixed(2)} BDT</Text>
                                        </Table.Summary.Cell>
                                      </Table.Summary.Row>
                                    </Table.Summary>
                                  )}
                                  style={{ marginBottom: "24px" }}
                                />
                              ) : (
                                <div
                                  style={{
                                    textAlign: "center",
                                    padding: "24px 0",
                                    background: "#fafafa",
                                    borderRadius: "8px",
                                    border: "1px dashed #d9d9d9",
                                    marginBottom: "24px",
                                  }}
                                >
                                  <Text type="secondary" style={{ fontSize: "14px" }}>
                                    Please select at least one fee type from above to add details
                                  </Text>
                                </div>
                              )}
                            </>
                          )}
                        </Form.List>
                      </Card>
                      <Card>
                        <Row gutter={16}>
                          <Col span={24}>
                            {form.getFieldValue("epb_type_tabs")?.length === 0 && (
                              <Text type="warning">
                                No EPB types available. Please select an invoice first or contact admin.
                              </Text>
                            )}

                            {form.getFieldValue("epb_type_tabs")?.length > 0 && (
                              <>
                                <div style={{ marginBottom: "16px" }}>
                                  <Text strong style={{ fontSize: "16px" }}>
                                    Select EPB:
                                  </Text>
                                </div>

                                <div gutter={16} style={{ marginBottom: "24px" }}>
                                  <Form.Item
                                    noStyle
                                    shouldUpdate={(prevValues, currentValues) => {
                                      if (
                                        JSON.stringify(prevValues.entries?.[field.name]?.epb_cost) !==
                                        JSON.stringify(currentValues.entries?.[field.name]?.epb_cost)
                                      ) {
                                        return true;
                                      }

                                      if (
                                        JSON.stringify(prevValues.epb_type_tabs) !==
                                        JSON.stringify(currentValues.epb_type_tabs)
                                      ) {
                                        return true;
                                      }

                                      if (
                                        isEdit &&
                                        !prevValues.entries?.[field.name]?.invoice_id &&
                                        currentValues.entries?.[field.name]?.invoice_id
                                      ) {
                                        return true;
                                      }

                                      return false;
                                    }}
                                  >
                                    {() => {
                                      const epbCost =
                                        form.getFieldValue(["entries", field.name, "epb_cost"]) || [];
                                      const selectedValues = epbCost.map((item) => item.epb_type_id).filter(Boolean);

                                      return (
                                        <Checkbox.Group
                                          value={selectedValues}
                                          onChange={handleEpbSelectionChange(field.name)}
                                          style={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            width: "100%",
                                            gap: "12px",
                                          }}
                                        >
                                          {(form.getFieldValue("epb_type_tabs") || []).map((epb) => {
                                            const isSelected = selectedValues.includes(epb.id);

                                            return (
                                              <Checkbox
                                                key={epb.id}
                                                style={{
                                                  padding: "8px 16px",
                                                  border: `1px solid ${isSelected ? "#1890ff" : "#d9d9d9"}`,
                                                  borderRadius: "6px",
                                                  cursor: "pointer",
                                                  backgroundColor: isSelected ? "#e6f7ff" : "#ffffff",
                                                  position: "relative",
                                                  marginRight: "8px",
                                                  display: "flex",
                                                  boxShadow: isSelected
                                                    ? "0 2px 6px rgba(24,144,255,0.2)"
                                                    : "none",
                                                  transition: "all 0.3s ease",
                                                  marginBottom: "10px",
                                                }}
                                                value={epb.id}
                                                disabled={viewMode}
                                              >
                                                <span
                                                  style={{
                                                    fontWeight: isSelected ? "500" : "normal",
                                                    color: isSelected ? "#1890ff" : "#333",
                                                  }}
                                                >
                                                  {epb.name || epb.type}
                                                </span>
                                              </Checkbox>
                                            );
                                          })}
                                        </Checkbox.Group>
                                      );
                                    }}
                                  </Form.Item>
                                </div>
                              </>
                            )}
                          </Col>
                        </Row>

                        <Divider orientation="left" style={{ marginTop: "24px" }}>
                          EPB Cost Details
                        </Divider>
                        <Form.List name={[field.name, "epb_cost"]}>
                          {(epbFields) =>
                            epbFields.length > 0 ? (
                              <Card bordered={true} style={{ marginBottom: "24px" }}>
                                {epbFields.map((epbField) => {
                                  const epbCost = form.getFieldValue([
                                    "entries",
                                    field.name,
                                    "epb_cost",
                                  ])[epbField.name];
                                  return (
                                    <div key={epbField.key}>
                                      <div
                                        style={{
                                          padding: "12px",
                                          backgroundColor: "#d4f4dd",
                                          border: "1px solid #b7eb8f",
                                          fontWeight: "bold",
                                        }}
                                      >
                                        {epbCost?.epb_type_name || "N/A"}
                                      </div>

                                      <div
                                        style={{
                                          backgroundColor: "#fff",
                                          border: "1px solid #d9d9d9",
                                          borderTop: "none",
                                        }}
                                      >
                                        {options.attribute.length > 0
                                          ? options.attribute
                                              .filter((attr) => attr.is_required === "1")
                                              .map((attr) => {
                                                const attrFieldName = attr.name
                                                  .toLowerCase()
                                                  .replace(/\s+/g, "_");

                                                return (
                                                  <Row
                                                    key={`${epbField.key}-${attr.id}`}
                                                    style={{
                                                      width: "100%",
                                                      borderBottom: "1px solid #d4d4d4",
                                                      padding: "8px",
                                                    }}
                                                    align={"middle"}
                                                  >
                                                    <Col span={12}>
                                                      <Text>{attr.name}</Text>
                                                    </Col>
                                                    <Col span={12}>
                                                      <Form.Item
                                                        name={[epbField.name, attrFieldName, "epb_type_id"]}
                                                        noStyle
                                                        initialValue={epbCost.epb_type_id}
                                                        hidden
                                                      >
                                                        <Input hidden />
                                                      </Form.Item>
                                                      <Form.Item
                                                        name={[epbField.name, attrFieldName, "epb_attribute_id"]}
                                                        noStyle
                                                        initialValue={attr.id}
                                                        hidden
                                                      >
                                                        <Input hidden />
                                                      </Form.Item>
                                                      <Form.Item
                                                        name={[epbField.name, attrFieldName, "amount"]}
                                                        rules={[
                                                          {
                                                            required: true,
                                                            message: `Please enter ${attr.name} amount`,
                                                          },
                                                        ]}
                                                      >
                                                        {attr.field_type === "textarea" ? (
                                                          <Input.TextArea
                                                            placeholder={`Enter ${attr.name}`}
                                                            disabled={viewMode}
                                                            autoSize={{
                                                              minRows: 1,
                                                              maxRows: 4,
                                                            }}
                                                            style={{ width: "100%" }}
                                                          />
                                                        ) : (
                                                          <InputNumber
                                                            placeholder={`Enter ${attr.name}`}
                                                            disabled={viewMode}
                                                            min={0}
                                                            precision={2}
                                                            formatter={(v) =>
                                                              `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                                            }
                                                            parser={(v) => v.replace(/\$\s?|(,*)/g, "")}
                                                            style={{ width: "100%" }}
                                                          />
                                                        )}
                                                      </Form.Item>
                                                    </Col>
                                                  </Row>
                                                );
                                              })
                                          : "No attributes found"}
                                      </div>
                                    </div>
                                  );
                                })}

                                <Row
                                  style={{
                                    padding: "12px",
                                    backgroundColor: "#fff",
                                    border: "1px solid #d9d9d9",
                                    marginBottom: "8px",
                                  }}
                                >
                                  <Col span={12}>
                                    <strong>REX Declaration</strong>
                                  </Col>
                                  <Col span={12} style={{ textAlign: "right" }}>
                                    <InputNumber
                                      placeholder="Enter amount"
                                      disabled={viewMode}
                                      min={0}
                                      precision={2}
                                      value={rexDeclarationValue}
                                      formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                      parser={(v) => v.replace(/\$\s?|(,*)/g, "")}
                                      style={{ width: "100%" }}
                                      onChange={(value) => {
                                        setRexDeclarationValues((prev) => {
                                          const next = [...prev];
                                          next[field.name] = value;
                                          return next;
                                        });

                                        const entries = [...(form.getFieldValue("entries") || [])];
                                        entries[field.name] = {
                                          ...entries[field.name],
                                          rex_declaration: value,
                                        };
                                        form.setFieldsValue({ entries });

                                        form
                                          .validateFields([["entries", field.name, "rex_declaration"]])
                                          .catch(() => {});
                                      }}
                                    />
                                  </Col>
                                </Row>

                                <Row
                                  style={{
                                    padding: "12px",
                                    backgroundColor: "#fafafa",
                                    border: "1px solid #d9d9d9",
                                    fontWeight: "bold",
                                    marginTop: "8px",
                                  }}
                                >
                                  <Col span={12}>
                                    <strong>Grand Total</strong>
                                  </Col>
                                  <Col span={12} style={{ textAlign: "right" }}>
                                    <Form.Item shouldUpdate={() => true} noStyle>
                                      {() => {
                                        const calculateGrandTotal = () => {
                                          const epbCosts =
                                            form.getFieldValue(["entries", field.name, "epb_cost"]) || [];

                                          let epbTotal = 0;

                                          if (epbCosts.length > 0) {
                                            epbCosts.forEach((cost) => {
                                              const requiredAttributes = options.attribute.filter(
                                                (attr) => attr.is_required === "1",
                                              );

                                              requiredAttributes.forEach((attr) => {
                                                if (attr.field_type === "textarea") {
                                                  return;
                                                }

                                                const fieldName = attr.name.toLowerCase().replace(/\s+/g, "_");
                                                const fieldValue = cost[fieldName];

                                                if (
                                                  fieldValue &&
                                                  typeof fieldValue === "object" &&
                                                  fieldValue.amount
                                                ) {
                                                  epbTotal += parseFloat(fieldValue.amount) || 0;
                                                }
                                              });
                                            });
                                          }

                                          const rexAmount = parseFloat(rexDeclarationValue) || 0;

                                          const costs =
                                            form.getFieldValue(["entries", field.name, "cost"]) || [];
                                          const costTotal = costs.reduce((sum, cost) => {
                                            return sum + (parseFloat(cost.exim_charge_amount) || 0);
                                          }, 0);

                                          return epbTotal + rexAmount + costTotal;
                                        };

                                        const grandTotal = calculateGrandTotal();

                                        return <strong>{parseFloat(grandTotal).toFixed(2)} BDT</strong>;
                                      }}
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </Card>
                            ) : (
                              <div
                                style={{
                                  textAlign: "center",
                                  padding: "24px 0",
                                  background: "#fafafa",
                                  borderRadius: "8px",
                                  border: "1px dashed #d9d9d9",
                                  marginBottom: "24px",
                                }}
                              >
                                <Text type="secondary">Please select at least one EPB type to add cost details</Text>
                              </div>
                            )
                          }
                        </Form.List>
                      </Card>
                    </div>
                  );
                })}

                {allowMultiple && (
                  <Button
                    type="dashed"
                    onClick={() => {
                      add({ cost: [], epb_cost: [] });
                      setFileLists((prev) => [...prev, []]);
                      setRexDeclarationValues((prev) => [...prev, 0]);
                    }}
                    icon={<PlusOutlined />}
                    style={{ width: "100%", marginTop: "8px" }}
                  >
                    Add Row
                  </Button>
                )}
              </>
            )}
          </Form.List>
        </Form>
      </Card>
    </div>
  );
});

CertificateOfOriginFeeForm.displayName = "CertificateOfOriginFeeForm";

export default CertificateOfOriginFeeForm;
